"use client";

import { useState, useEffect, Suspense } from "react";
import type { ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NextIntlClientProvider } from "next-intl";
import { PostHogProvider } from "@/lib/analytics";
import { AnalyticsIdentifier } from "@/components/analytics/AnalyticsIdentifier";
import { CustomerIOPageTracker } from "@/components/analytics/CustomerIOPageTracker";
import { MetaPixelTracker } from "@/components/analytics/MetaPixelTracker";
import messages from "@/i18n/messages/en.json";
import { TimezoneSync } from "@/components/auth/TimezoneSync";
import { authService } from "@/lib/authService";
import { Toaster } from "sonner";

export function Providers({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            gcTime: 0,
            staleTime: 0,
          },
          mutations: {
            retry: 1,
          },
        },
      })
  );
  useEffect(() => {
    const checkAuth = () => {
      setIsAuthenticated(authService.isAuthenticated());
    };
    checkAuth();

    window.addEventListener("auth-change", checkAuth);
    return () => window.removeEventListener("auth-change", checkAuth);
  }, []);

  return (
    <PostHogProvider>
      <NextIntlClientProvider locale="en" messages={messages} timeZone="UTC">
        <QueryClientProvider client={queryClient}>
          <AnalyticsIdentifier />
          <Suspense fallback={null}>
            <CustomerIOPageTracker />
          </Suspense>
          <Suspense fallback={null}>
            <MetaPixelTracker />
          </Suspense>
          {isAuthenticated && <TimezoneSync />}
          {children}
          <Toaster
            position="top-right"
            richColors
            toastOptions={{
              style: {
                background: "#FDF2F8",
                color: "#000000",
                border: "1px solid #ED5AA9",
              },
            }}
          />
        </QueryClientProvider>
      </NextIntlClientProvider>
    </PostHogProvider>
  );
}
